import type { Draft } from '@reduxjs/toolkit';
import type { RootState } from '@src/store/store';
import type {
  ExploreHealthCode,
  ExploreDebugCandidate,
  ExploreDebugMapResponse,
  ExploreDebugPairResponse,
  ExploreDebugViewerResponse,
} from 'src/types/explore-debug';

import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

type Resource<T> = {
  data: T | null;
  status: Status;
  error: string;
  /** requestId of the thunk whose result is (or will be) in `data`. */
  requestId: string | null;
};

export interface ExploreDebugState {
  map: Resource<ExploreDebugMapResponse>;
  viewer: Resource<ExploreDebugViewerResponse>;
  pair: Resource<ExploreDebugPairResponse>;
}

const emptyResource = <T>(): Resource<T> => ({
  data: null,
  status: 'idle',
  error: '',
  requestId: null,
});

export const initialState: ExploreDebugState = {
  map: emptyResource(),
  viewer: emptyResource(),
  pair: emptyResource(),
};

// ----------------------------------------------------------------------
// Our own accounts, kept out of « Debug › Explore »
//
// Production doubles as the UI-testing environment, so the developers' own
// rows sit in the deck, on the map and in the candidate tables next to real
// members. That is noise in a tool whose whole job is to explain why a real
// member does or does not see another real member.
//
// Filtered here, where the payload lands, rather than in each view: the
// viewer picker, the candidates table, the map and the edges tables all read
// the same `map.users`, so one place covers the tab and a view added later
// inherits it for free.
//
// NOT the same thing as the backend's `systemAccount` flag. That one hides a
// row from the APP through a Prisma extension which deliberately does not
// cover raw SQL — and these admin routes are raw SQL, so it would do nothing
// here. It also scopes `findUnique`, which would stop the account being
// loaded at all; these are working test accounts and must keep working.
//
// Add an id to hide another dev. Ids, not usernames, because a username can
// be changed from inside the app and would silently stop matching.
// ----------------------------------------------------------------------

export const HIDDEN_DEBUG_USER_IDS: readonly string[] = ['da02f74d-d7dd-4d56-8508-385cccf2ab11'];

function withoutHiddenUsers(data: ExploreDebugMapResponse): ExploreDebugMapResponse {
  const hidden = new Set(HIDDEN_DEBUG_USER_IDS);
  if (hidden.size === 0) return data;

  const users = data.users.filter((user) => !hidden.has(user.id));
  // Nothing to do is the common case on dev, where these rows do not exist.
  if (users.length === data.users.length) return data;

  const matches = data.matches.filter(
    (edge) => !hidden.has(edge.userAId) && !hidden.has(edge.userBId)
  );
  const keepOnfit = (edge: { fromUserId: string; toUserId: string }) =>
    !hidden.has(edge.fromUserId) && !hidden.has(edge.toUserId);
  const onfits = data.onfits.filter(keepOnfit);

  // The counts are recomputed rather than passed through. Filtering the lists
  // while keeping the backend's totals would leave a header saying 573 above a
  // table holding 572, and no way for the reader to tell which one is wrong.
  // Every field below is derived from the same `ExploreDebugUser` the table
  // renders, so the two cannot drift.
  const health = Object.fromEntries(
    Object.keys(data.counts.health).map((code) => [code, 0])
  ) as Record<ExploreHealthCode, number>;
  users.forEach((user) => {
    user.health.forEach((code) => {
      health[code] = (health[code] ?? 0) + 1;
    });
  });

  return {
    ...data,
    users,
    matches,
    onfits,
    recentOnfits: data.recentOnfits.filter(keepOnfit),
    counts: {
      ...data.counts,
      total: users.length,
      withLocation: users.filter((user) => user.location !== null).length,
      configured: users.filter((user) => user.configured).length,
      banned: users.filter((user) => user.banned).length,
      withFilterRow: users.filter((user) => user.hasFilterRow).length,
      premium: users.filter((user) => user.subscriptionTier === 'PREMIUM').length,
      matches: matches.length,
      onfits: onfits.length,
      health,
    },
  };
}

/**
 * The same exclusion for one viewer's explanation.
 *
 * `map.users` and `viewer.candidates` are different payloads from different
 * endpoints, so filtering the first does nothing for the second: a hidden dev
 * would still turn up as a row in every other tester's deck, which is the
 * listing the tool is actually read for.
 *
 * `summary` and `reasonHistogram` are recomputed from the surviving rows for
 * the same reason the map counts are, and the viewer themself is never
 * filtered — asking why THIS person sees what they see is the whole question.
 */
function withoutHiddenCandidates(data: ExploreDebugViewerResponse): ExploreDebugViewerResponse {
  const hidden = new Set(HIDDEN_DEBUG_USER_IDS);
  if (hidden.size === 0) return data;

  const candidates = data.candidates.filter((candidate) => !hidden.has(candidate.user.id));
  if (candidates.length === data.candidates.length) return data;

  const count = (predicate: (candidate: ExploreDebugCandidate) => boolean) =>
    candidates.filter(predicate).length;

  const histogram = new Map(data.reasonHistogram.map((row) => [row.code, { ...row, count: 0 }]));
  candidates.forEach((candidate) => {
    const seen = new Set<string>();
    candidate.reasons.forEach((reason) => {
      // A reason is counted once per candidate: the histogram answers "how many
      // people did this rule affect", not "how many times did it fire".
      if (seen.has(reason.code)) return;
      seen.add(reason.code);
      const row = histogram.get(reason.code);
      if (row) row.count += 1;
    });
  });

  return {
    ...data,
    candidates,
    summary: {
      ...data.summary,
      total: candidates.length,
      inDeck: count((c) => c.verdict === 'IN_DECK'),
      hiddenByApp: count((c) => c.verdict === 'HIDDEN_BY_APP'),
      filtered: count((c) => c.verdict === 'FILTERED'),
      excluded: count((c) => c.verdict === 'EXCLUDED'),
      serverIncluded: count((c) => c.serverIncluded),
      noLocation: count((c) => c.user.location === null),
      mutualServerIncluded: count((c) => c.mutualServerIncluded),
      mutualOnPhone: count((c) => c.mutualOnPhone),
    },
    reasonHistogram: [...histogram.values()],
  };
}

// ----------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------

export const loadExploreMap = createAsyncThunk<
  ExploreDebugMapResponse,
  { staleAfterDays?: number } | undefined
>('exploreDebug/loadMap', async (args) => {
  const response = await api.get<ExploreDebugMapResponse>(endpoints.admin.debug.explore.map, {
    params: args?.staleAfterDays ? { staleAfterDays: args.staleAfterDays } : undefined,
  });
  return response.data;
});

export const loadExploreViewer = createAsyncThunk<
  ExploreDebugViewerResponse,
  { viewerId: string; crossCheck?: boolean; staleAfterDays?: number }
>('exploreDebug/loadViewer', async ({ viewerId, crossCheck, staleAfterDays }) => {
  const response = await api.get<ExploreDebugViewerResponse>(
    endpoints.admin.debug.explore.viewer(viewerId),
    {
      params: {
        ...(crossCheck === false && { crossCheck: 'false' }),
        ...(staleAfterDays && { staleAfterDays }),
      },
    }
  );
  return response.data;
});

export const loadExplorePair = createAsyncThunk<
  ExploreDebugPairResponse,
  { viewerId: string; targetId: string; crossCheck?: boolean }
>('exploreDebug/loadPair', async ({ viewerId, targetId, crossCheck }) => {
  const response = await api.get<ExploreDebugPairResponse>(
    endpoints.admin.debug.explore.pair(viewerId, targetId),
    { params: crossCheck === false ? { crossCheck: 'false' } : undefined }
  );
  return response.data;
});

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

// A viewer switch can overtake an earlier, slower request. Every resource
// remembers the requestId it is waiting for, and a result from any other
// request is dropped on the floor rather than overwriting fresher state.
function pending<T>(resource: Draft<Resource<T>>, requestId: string) {
  resource.status = 'loading';
  resource.error = '';
  resource.requestId = requestId;
}

function fulfilled<T>(resource: Draft<Resource<T>>, requestId: string, data: T) {
  if (resource.requestId !== requestId) return;
  resource.status = 'succeeded';
  resource.data = data as Draft<T>;
  resource.error = '';
}

function rejected<T>(
  resource: Draft<Resource<T>>,
  requestId: string,
  message: string | undefined,
  fallback: string
) {
  if (resource.requestId !== requestId) return;
  resource.status = 'failed';
  resource.error = message || fallback;
}

const slice = createSlice({
  name: 'exploreDebug',
  initialState,
  reducers: {
    clearExploreViewer(state) {
      state.viewer = emptyResource();
      state.pair = emptyResource();
    },
    clearExplorePair(state) {
      state.pair = emptyResource();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadExploreMap.pending, (state, action) => {
        pending(state.map, action.meta.requestId);
      })
      .addCase(loadExploreMap.fulfilled, (state, action) => {
        fulfilled(state.map, action.meta.requestId, withoutHiddenUsers(action.payload));
      })
      .addCase(loadExploreMap.rejected, (state, action) => {
        rejected(
          state.map,
          action.meta.requestId,
          action.error.message,
          'Failed to load the explore map'
        );
      })
      .addCase(loadExploreViewer.pending, (state, action) => {
        pending(state.viewer, action.meta.requestId);
      })
      .addCase(loadExploreViewer.fulfilled, (state, action) => {
        fulfilled(state.viewer, action.meta.requestId, withoutHiddenCandidates(action.payload));
      })
      .addCase(loadExploreViewer.rejected, (state, action) => {
        rejected(
          state.viewer,
          action.meta.requestId,
          action.error.message,
          'Failed to explain this viewer'
        );
      })
      .addCase(loadExplorePair.pending, (state, action) => {
        pending(state.pair, action.meta.requestId);
      })
      .addCase(loadExplorePair.fulfilled, (state, action) => {
        fulfilled(state.pair, action.meta.requestId, action.payload);
      })
      .addCase(loadExplorePair.rejected, (state, action) => {
        rejected(
          state.pair,
          action.meta.requestId,
          action.error.message,
          'Failed to explain this pair'
        );
      });
  },
});

export const { clearExploreViewer, clearExplorePair } = slice.actions;
export const exploreDebugReducer = slice.reducer;

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectExploreMap = (state: RootState) => state.exploreDebug.map;
export const selectExploreViewer = (state: RootState) => state.exploreDebug.viewer;
export const selectExplorePair = (state: RootState) => state.exploreDebug.pair;

export const selectMapUsersById = createSelector(
  (state: RootState) => state.exploreDebug.map.data?.users,
  (users) => new Map((users ?? []).map((user) => [user.id, user]))
);
